import $ from "jquery";
import {gsap} from "gsap/all";

export class iFrameController {
  constructor() {

    this.iframeContainer = $(".webpage-container .webpage-iframe-container").get(0);
    this.iframe = $(".webpage-container .webpage-iframe-container iframe").get(0);
    this.overlayEl = $(".webpage-container .iframe-overlay").get(0);
    this.highlightEl = $(".webpage-container .iframe-highlight").get(0);
    this.tooltipEl = $(".webpage-container .iframe-tooltip").get(0);
    this.tooltipTextEl = $(".webpage-container .iframe-tooltip .tooltip-box").get(0);

    this.loggedIn = false;
    this._loginCounter = 0;
    this._lastInteractAt = 0;
    this._aborted = false;
    this._sessionId = 0;
    this._loaded = false;
    this._loadWaiters = [];
    this.highlightGutter = 4; // px
    this.tooltipGap = 12; // px

    this.debug = true;

    // State
    this._highlightClickCount = 0;
    this._backClickCount = 0;
    this._skipResolver = null;
    this._skipPromise = null;
    this._resetSkip();

    // Dynamically created elements
    this.backButtonEl = null;
    this._extraTooltips = [];

    this.init();
  }

  init() {
    // Listen for messages (e.g. new-window)
    window.addEventListener('message', (event) => {
      const msg = event.data;
      if (msg.from === 'ret-puppeteer' && msg.type === 'new-window') {
        this.log(`← new-window: ${msg.value}`);
        this.iframe.src = msg.value;
      }
    });

    // Track iframe load state so we can auto login if necessary
    this.iframe.addEventListener('load', async () => {
      this._loginCounter++;
      const loginCounter = this._loginCounter;
      this._loaded = this.iframe.src !== 'about:blank' && this.iframe.src !== '';
      if (this._loaded) this._flushLoadWaiters(true);
      this.log('iframe loaded');
      this._injectOverrideStyles();
      this._bindIframeInteractionListeners();
      await this.autoLogin(loginCounter);
    });

    // Track highlight clicks via counter (used by waitForHighlightClick)
    this.highlightEl.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this._highlightClickCount++;
    });

    // Prevent highlight/overlay from stealing focus from the iframe on mousedown
    this.highlightEl.addEventListener('mousedown', (e) => e.preventDefault());
    this.overlayEl.addEventListener('mousedown', (e) => e.preventDefault());

    this._createDynamicElements();

  }

  // Reset the inactivity timer and broadcast active state whenever the user
  // interacts with the iframe content. Requires --disable-web-security since
  // the iframe is cross-origin and we reach into its document directly.
  _bindIframeInteractionListeners() {
    // Restore focus to the parent window so global keypress handlers (1-5 scenario loads) keep working
    window.focus();
    const doc = this.iframe.contentDocument;
    if (!doc) return;
    ['pointerdown', 'mousedown', 'touchstart'].forEach((evt) => {
      doc.addEventListener(evt, this._onIframeInteract, {capture: true, passive: true});
    });
  }

  // Inject custom CSS into the iframe document. Runs on every load event, since
  // each navigation replaces the document and wipes any previously injected styles.
  _injectOverrideStyles() {
    const doc = this.iframe.contentDocument;
    if (!doc || !doc.head) return;
    if (doc.getElementById('bbai-injected-styles')) return;
    const style = doc.createElement('style');
    style.id = 'bbai-injected-styles';
    style.textContent = "[for$='file-upload-input'] { display: none !important; }";
    doc.head.appendChild(style);
  }

  _onIframeInteract = () => {
    // Debounce: a single tap fires pointerdown + mousedown + touchstart
    const now = Date.now();
    if (now - this._lastInteractAt < 250) return;
    this._lastInteractAt = now;

    if (app.currentScreen !== 4) return;
    app.resetTimer();
    // As requested by Sam on 2026-05-19, keep sending active on interactions
    app.sendScreenBridgeMessage("/send active");
  }

  _createDynamicElements() {
    const container = this.iframeContainer;

    // Back button (for waitForBackClick)
    const backBtn = document.createElement('button');
    backBtn.className = 'iframe-back-button';
    backBtn.textContent = '← Back';
    backBtn.style.cssText = `
      position: absolute;
      bottom: 16px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 30;
      background: #6c63ff;
      color: #fff;
      border: none;
      border-radius: 8px;
      padding: 14px 40px;
      font-size: 16px;
      font-weight: bold;
      cursor: pointer;
      display: none;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    `;
    backBtn.addEventListener('click', () => {
      this._backClickCount++;
    });
    container.appendChild(backBtn);
    this.backButtonEl = backBtn;

    // Persistent cutout layer (highlight + tooltip clones so they survive clearHighlight)
    this.cutoutHighlightEl = this.highlightEl.cloneNode(false);
    this.cutoutHighlightEl.style.display = 'none';
    this.cutoutHighlightEl.style.pointerEvents = 'none';
    container.appendChild(this.cutoutHighlightEl);

    this.cutoutTooltipEl = this.tooltipEl.cloneNode(true);
    this.cutoutTooltipEl.style.display = 'none';
    this.cutoutTooltipEl.style.pointerEvents = 'none';
    container.appendChild(this.cutoutTooltipEl);
    this.cutoutTooltipTextEl = this.cutoutTooltipEl.querySelector('.tooltip-box');
  }

  loadBaseUrl() {
    this._aborted = false;
    this._loaded = false;
    this._sessionId++;
    this.iframe.src = app.settings.cbms_iframe_url;
  }

  unload() {
    this._loaded = false;
    this._sessionId++;
    this._flushLoadWaiters(false);
    this.iframe.src = 'about:blank';
  }

  _flushLoadWaiters(loaded) {
    const waiters = this._loadWaiters;
    this._loadWaiters = [];
    for (const resolve of waiters) resolve(loaded);
  }

  waitForBaseLoad({timeout = 30000} = {}) {
    if (this._loaded) return Promise.resolve(true);
    return new Promise((resolve) => {
      let done = false;
      const finish = (loaded) => {
        if (done) return;
        done = true;
        const idx = this._loadWaiters.indexOf(finish);
        if (idx !== -1) this._loadWaiters.splice(idx, 1);
        if (!loaded) this.log(`waitForBaseLoad released without success (timeout or unload)`);
        resolve(loaded);
      };
      this._loadWaiters.push(finish);
      setTimeout(() => finish(false), timeout);
    });
  }

  async waitForLogin({timeout = 6000000, interval = 500} = {}) {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      const url = await this.getUrl();
      if (url && !url.includes('/auth/sign-in')) {
        this.loggedIn = true;
        return true;
      }
      await this.sleep(interval);
    }
    this.log('waitForLogin timed out');
    return false;
  }

  // --- Auto login process ---
  async autoLogin(loginCounter) {
    // Skip auto login when the iframe was deliberately unloaded
    if (this.iframe.src === 'about:blank' || this.iframe.src === '') return;

    await this.sleep(3000); // allow redirect to complete if already logged in

    // We use a login counter to prevent multiple auto login tries if scenarios are
    // launched at quick intervals
    if (loginCounter !== this._loginCounter) return;

    const currentUrl = await this.getUrl();
    if (loginCounter !== this._loginCounter || !currentUrl) return;

    if (!currentUrl.includes('/auth/sign-in')) {
      this.loggedIn = true;
      return;
    }

    this.loggedIn = false;
    this.log('Not logged in, logging in now');
    this.setSelectorValue({selector: 'input[name="username"]', selectorIndex: 0, value: app.settings.cbms_iframe_credentials.username});
    this.setSelectorValue({selector: 'input[name="password"]', selectorIndex: 0, value: app.settings.cbms_iframe_credentials.password});
    this.sendMessage({selector: 'input[name="cookie"]:not(:checked)', selectorIndex: 0, type: 'click-element'});

    await this.sleep(1000); // wait for the button to be ready
    if (loginCounter !== this._loginCounter) return;
    this.sendMessage({selector: 'button[type="submit"]', selectorIndex: 0, type: 'click-element'});
  }

  // --- Overlay & highlight state ---

  setBlockOverlay(enabled) {
    if (!this.overlayEl) return;
    if (enabled === undefined) {
      enabled = this.overlayEl.style.display === 'none';
    }
    this.overlayEl.style.display = enabled ? 'block' : 'none';
    this.iframe.style.pointerEvents = enabled ? 'none' : 'auto';
  }

  setOverlayCutout(rect) {
    if (!this.overlayEl) return;
    if (!rect) {
      this.overlayEl.style.clipPath = '';
      this.iframe.style.pointerEvents = 'none';
      return;
    }
    this.iframe.style.pointerEvents = 'auto';
    const {width: W, height: H} = this.iframeContainer.getBoundingClientRect();
    const {x, y, width, height} = rect;
    const x2 = x + width;
    const y2 = y + height;
    this.overlayEl.style.clipPath =
      `polygon(evenodd, 0 0, ${W}px 0, ${W}px ${H}px, 0 ${H}px, 0 0, ` +
      `${x}px ${y}px, ${x}px ${y2}px, ${x2}px ${y2}px, ${x2}px ${y}px, ${x}px ${y}px)`;
  }

  setHighlightClickThrough(enabled) {
    this.iframe.style.pointerEvents = enabled ? 'auto' : 'none';
    this.highlightEl.style.pointerEvents = enabled ? 'none' : 'auto';
  }

  // --- Skip ---

  _resetSkip() {
    this._skipPromise = new Promise((resolve) => {
      this._skipResolver = resolve;
    });
  }

  skip() {
    this._aborted = true;
    this._sessionId++;
    this._skipResolver();
  }

  _raceWithSkip(promise) {
    return Promise.race([
      promise,
      this._skipPromise.then(() => {
        this._resetSkip();
      }),
    ]);
  }

  // --- Wait helpers ---

  waitForHighlightClick() {
    const initial = this._highlightClickCount;
    let cancelled = false;
    this._skipPromise.then(() => {
      cancelled = true;
    });
    return this._raceWithSkip(new Promise((resolve) => {
      const poll = () => {
        if (cancelled) return;
        if (this._highlightClickCount !== initial) {
          app.audio.play("audio/ButtonOnClick_260423.wav", 1, true)
          resolve();
        } else {
          setTimeout(poll, 200);
        }
      };
      poll();
    }));
  }

  waitForBackClick({message = null} = {}) {
    this.backButtonEl.style.display = 'block';
    if (message) {
      const btn = this.backButtonEl.getBoundingClientRect();
      const cont = this.iframeContainer.getBoundingClientRect();
      this.showTooltip({
        x: btn.left - cont.left,
        y: btn.top - cont.top,
        width: btn.width,
        height: btn.height
      }, message);
    }
    const initial = this._backClickCount;
    let cancelled = false;
    this._skipPromise.then(() => {
      cancelled = true;
    });
    return this._raceWithSkip(new Promise((resolve) => {
      const poll = () => {
        if (cancelled) return;
        if (this._backClickCount !== initial) {
          this.backButtonEl.style.display = 'none';
          if (message) this.clearHighlight();
          resolve();
        } else {
          setTimeout(poll, 200);
        }
      };
      poll();
    }));
  }

  async waitForFreeRoaming() {
    this.clearHighlight();
    this.setBlockOverlay(false);
    this.setHighlightClickThrough(true);

    // Never resolves on its own — only via skip() (triggered by Scenario.reset / cancelScenario)
    await this._raceWithSkip(new Promise(() => {
    }));

    this.setHighlightClickThrough(false);
    this.setBlockOverlay(true);
  }

  waitForIframeLoad({timeout = 30000} = {}) {
    return new Promise((resolve) => {
      let done = false;
      const finish = (loaded) => {
        if (done) return;
        done = true;
        this.iframe.removeEventListener('load', onLoad);
        if (!loaded) this.log(`waitForIframeLoad timed out after ${timeout}ms`);
        resolve(loaded);
      };
      const onLoad = () => finish(true);
      this.iframe.addEventListener('load', onLoad);
      setTimeout(() => finish(false), timeout);
    });
  }

  waitForAnyClick() {
    return this._raceWithSkip(new Promise((resolve) => {
      let resolved = false;

      const cleanup = () => {
        if (resolved) return;
        resolved = true;
        window.removeEventListener('message', listener);
      };

      const listener = (event) => {
        const msg = event.data;
        if (msg.from === 'ret-puppeteer' && msg.type === 'clicked') {
          cleanup();
          setTimeout(resolve, 100);
        }
      };

      window.addEventListener('message', listener);
      this.sendMessage({selector: '', type: 'wait-for-click'});

      // Timeout fallback after 30s
      setTimeout(() => {
        if (!resolved) {
          cleanup();
          resolve();
        }
      }, 30000);
    }));
  }

  blurElement({selector, selectorIndex = 0}) {
    this.sendMessage({selector, selectorIndex, type: 'blur-element'});
  }

  // --- Selector text ---

  async getSelectorText({selector, selectorIndex = 0}) {
    this.sendMessage({selector, selectorIndex, type: 'get-text'});
    const resp = await this.waitForResponse((m) => m.type === 'text');
    return resp?.value ?? null;
  }

  // --- Goto helpers (from app.tsx) ---

  _handleMissingElement(description, continueOnError) {
    const errMsg = `Element not found: ${description}`;
    if (continueOnError) {
      this.log(errMsg);
      return true;
    }
    throw new Error(errMsg);
  }

  _stale(session) {
    return session !== this._sessionId;
  }

  async gotoSelectorAndWaitForClick({selector, selectorIndex = 0, shape = 'rectangle', scroll = true, message = null, padding, continueOnError = false}) {
    const session = this._sessionId;
    this.sendMessage({selector, selectorIndex, type: 'get-rectangle'});
    let resp = await this.waitForResponse(null, {timeout: 3000});
    if (this._stale(session)) return;
    if (!resp?.value) return this._handleMissingElement(`"${selector}" index: ${selectorIndex}`, continueOnError);

    if (scroll) {
      this.sendMessage({selector, selectorIndex, type: 'scroll-to-element'});
      await this.sleep(500);
      if (this._stale(session)) return;

      this.sendMessage({selector, selectorIndex, type: 'get-rectangle'});
      resp = await this.waitForResponse(null, {timeout: 3000});
      if (this._stale(session)) return;
    }

    this.showHighlight(resp?.value, shape, message, padding);

    this.log('Waiting for user click on highlight...');
    await this.waitForHighlightClick();
    if (this._stale(session)) return;

    this.sendMessage({selector, selectorIndex, type: 'click-element'});
    this.clearHighlight();
  }

  async gotoSelectorAndAutoClick({selector, selectorIndex = 0, shape = 'rectangle', scroll = true, highlightDuration = 1000, padding, continueOnError = false}) {
    const session = this._sessionId;
    this.sendMessage({selector, selectorIndex, type: 'get-rectangle'});
    let resp = await this.waitForResponse(null, {timeout: 3000});
    if (this._stale(session)) return;
    if (!resp?.value) return this._handleMissingElement(`"${selector}" index: ${selectorIndex}`, continueOnError);

    if (scroll) {
      this.sendMessage({selector, selectorIndex, type: 'scroll-to-element'});
      await this.sleep(500);
      if (this._stale(session)) return;

      this.sendMessage({selector, selectorIndex, type: 'get-rectangle'});
      resp = await this.waitForResponse(null, {timeout: 3000});
      if (this._stale(session)) return;
    }

    this.showHighlight(resp?.value, shape, null, padding);
    await this.sleep(highlightDuration);
    if (this._stale(session)) return;

    this.sendMessage({selector, selectorIndex, type: 'click-element'});
    this.clearHighlight();
  }

  async gotoElementByTextAndWaitForClick({selector, selectorIndex = 0, textValue, shape = 'rectangle', scroll = true, message = null, padding, continueOnError = false}) {
    const session = this._sessionId;
    this.sendMessage({selector, selectorIndex, type: 'get-rectangle-by-text', value: textValue});
    let resp = await this.waitForResponse(null, {timeout: 3000});
    if (this._stale(session)) return;
    if (!resp?.value) return this._handleMissingElement(`text "${textValue}"`, continueOnError);

    if (scroll) {
      this.sendMessage({selector, selectorIndex, type: 'scroll-to-element-by-text', value: textValue});
      await this.sleep(500);
      if (this._stale(session)) return;

      this.sendMessage({selector, selectorIndex, type: 'get-rectangle-by-text', value: textValue});
      resp = await this.waitForResponse(null, {timeout: 3000});
      if (this._stale(session)) return;
    }

    this.showHighlight(resp?.value, shape, message, padding);
    await this.waitForHighlightClick();
    if (this._stale(session)) return;

    this.sendMessage({selector, selectorIndex, type: 'click-element-by-text', value: textValue});
    this.clearHighlight();
  }

  async gotoElementByTextAndAutoClick({selector, selectorIndex = 0, textValue, shape = 'rectangle', scroll = true, highlightDuration = 1000, showHighlight = true, padding, continueOnError = false}) {
    const session = this._sessionId;
    this.sendMessage({selector, selectorIndex, type: 'get-rectangle-by-text', value: textValue});
    let resp = await this.waitForResponse(null, {timeout: 3000});
    if (this._stale(session)) return;
    if (!resp?.value) return this._handleMissingElement(`text "${textValue}"`, continueOnError);

    if (scroll) {
      this.sendMessage({selector, selectorIndex, type: 'scroll-to-element-by-text', value: textValue});
      await this.sleep(500);
      if (this._stale(session)) return;

      this.sendMessage({selector, selectorIndex, type: 'get-rectangle-by-text', value: textValue});
      resp = await this.waitForResponse(null, {timeout: 3000});
      if (this._stale(session)) return;
    }

    if (showHighlight) {
      this.showHighlight(resp?.value, shape, null, padding);
      await this.sleep(highlightDuration);
      if (this._stale(session)) return;
    }

    this.sendMessage({selector, selectorIndex, type: 'click-element-by-text', value: textValue});

    if (showHighlight) this.clearHighlight();
  }

  showCutout({rectangle, shape = 'rectangle', message = null, padding = this.highlightGutter} = {}) {
    if (!rectangle) return;

    // Position the persistent highlight clone
    const el = this.cutoutHighlightEl;
    const size = shape === 'circle' ? Math.max(rectangle.width, rectangle.height) + 2 * padding : null;
    gsap.killTweensOf(el);
    el.style.display = 'block';
    el.style.left = (shape === 'circle' ? rectangle.x + rectangle.width / 2 - size / 2 : rectangle.x - padding) + 'px';
    el.style.top = (shape === 'circle' ? rectangle.y + rectangle.height / 2 - size / 2 : rectangle.y - padding) + 'px';
    el.style.width = (size ?? (rectangle.width + 2 * padding)) + 'px';
    el.style.height = (size ?? (rectangle.height + 2 * padding)) + 'px';
    el.style.borderRadius = shape === 'circle' ? '50%' : '4px';
    gsap.fromTo(el, {opacity: 0}, {opacity: 1, duration: 0.3, ease: "power2.out"});

    if (message) {
      this._renderTooltip(this.cutoutTooltipEl, this.cutoutTooltipTextEl, rectangle, message);
    }

    this.setOverlayCutout(rectangle);
  }

  clearCutout() {
    gsap.killTweensOf(this.cutoutHighlightEl);
    gsap.killTweensOf(this.cutoutTooltipEl);
    gsap.to(this.cutoutHighlightEl, {
      opacity: 0, duration: 0.25, ease: "power2.in",
      onComplete: () => {
        this.cutoutHighlightEl.style.display = 'none';
      }
    });
    gsap.to(this.cutoutTooltipEl, {
      opacity: 0, duration: 0.25, ease: "power2.in",
      onComplete: () => {
        this.cutoutTooltipEl.style.display = 'none';
      }
    });
    this.setOverlayCutout(null);
  }

  async gotoSelectorAndWaitForSelect({selectSelector, optionSelector, selectorIndex = 0, shape = 'rectangle', scroll = true, message = null, continueOnError = false}) {
    const session = this._sessionId;
    this.sendMessage({selector: selectSelector, selectorIndex, type: 'get-rectangle'});
    let resp = await this.waitForResponse(null, {timeout: 3000});
    if (this._stale(session)) return;
    if (!resp?.value) return this._handleMissingElement(`"${selectSelector}" index: ${selectorIndex}`, continueOnError);

    if (scroll) {
      this.sendMessage({selector: selectSelector, selectorIndex, type: 'scroll-to-element'});
      await this.sleep(500);
      if (this._stale(session)) return;

      this.sendMessage({selector: selectSelector, selectorIndex, type: 'get-rectangle'});
      resp = await this.waitForResponse(null, {timeout: 3000});
      if (this._stale(session)) return;
    }

    this.showHighlight(resp?.value, shape, message);
    this.setHighlightClickThrough(false);

    await this.waitForHighlightClick();
    if (this._stale(session)) return;
    this.clearHighlight();

    this.sendMessage({selector: selectSelector, selectorIndex, type: 'click-element'});
    await this.sleep(50);
    if (this._stale(session)) return;

    this.sendMessage({selector: optionSelector, selectorIndex, type: 'get-rectangle'});
    resp = await this.waitForResponse(null, {timeout: 3000});
    if (this._stale(session)) return;
    if (!resp?.value) {
      this.clearHighlight();
      this.blurElement({selector: selectSelector, selectorIndex});
      return this._handleMissingElement(`"${optionSelector}" index: ${selectorIndex}`, continueOnError);
    }

    this.showHighlight(resp.value, shape);
    this.setHighlightClickThrough(false);
    this.iframe.style.pointerEvents = 'none';

    await this.waitForHighlightClick();
    if (this._stale(session)) return;

    this.sendMessage({selector: optionSelector, selectorIndex, type: 'click-element'});

    this.clearHighlight();
    this.blurElement({selector: selectSelector, selectorIndex});
  }

  async gotoSelectorAndWaitForMultiSelect({selectSelector, optionSelector, selectorIndex = 0, shape = 'rectangle', continueOnError = false}) {
    const session = this._sessionId;
    this.sendMessage({selector: selectSelector, selectorIndex, type: 'get-rectangle'});
    let resp = await this.waitForResponse(null, {timeout: 3000});
    if (this._stale(session)) return;
    if (!resp?.value) return this._handleMissingElement(`"${selectSelector}" index: ${selectorIndex}`, continueOnError);
    this.showHighlight(resp.value, shape);
    this.setHighlightClickThrough(false);

    await this.waitForHighlightClick();
    if (this._stale(session)) return;

    this.setBlockOverlay(false);
    this.iframe.style.pointerEvents = 'none';
    await this.sleep(50);
    if (this._stale(session)) return;

    this.sendMessage({selector: selectSelector, selectorIndex, type: 'click-element'});
    await this.sleep(800);
    if (this._stale(session)) return;

    this.clearHighlight();

    this.sendMessage({selector: optionSelector, selectorIndex, type: 'get-rectangle'});
    resp = await this.waitForResponse(null, {timeout: 3000});
    if (this._stale(session)) return;
    this.showHighlight(resp?.value, shape);
    this.setHighlightClickThrough(true);
    this.iframe.style.pointerEvents = 'auto';

    this.sendMessage({selector: optionSelector, selectorIndex, type: 'wait-for-option-click'});
    await this.waitForResponse((m) => m.type === 'option-clicked');
    if (this._stale(session)) return;

    await this.sleep(200);
    if (this._stale(session)) return;
    this.clearHighlight();
    this.setHighlightClickThrough(false);
    this.iframe.style.pointerEvents = 'none';
    await this.sleep(100);
    if (this._stale(session)) return;

    this.blurElement({selector: selectSelector, selectorIndex});
    await this.sleep(300);
    if (this._stale(session)) return;

    this.setBlockOverlay(true);
  }

  async gotoSelectorAndLetUserClick({selector, selectorIndex = 0, shape = 'rectangle', continueOnError = false}) {
    const session = this._sessionId;
    this.sendMessage({selector, selectorIndex, type: 'get-rectangle'});
    const resp = await this.waitForResponse(null, {timeout: 3000});
    if (this._stale(session)) return;
    if (!resp?.value) return this._handleMissingElement(`"${selector}" index: ${selectorIndex}`, continueOnError);
    this.showHighlight(resp.value, shape);

    this.setBlockOverlay(false);
    this.setHighlightClickThrough(true);

    await this.waitForAnyClick();
    if (this._stale(session)) return;
    await this.sleep(500);
    if (this._stale(session)) return;

    this.clearHighlight();
    this.setHighlightClickThrough(false);
    await this.sleep(100);
    if (this._stale(session)) return;
    this.setBlockOverlay(true);
  }

  async gotoSelectorAndWaitForValueSet({selector, selectorIndex = 0, value = '', scroll = true, message = null, continueOnError = false}) {
    const session = this._sessionId;
    this.sendMessage({selector, selectorIndex, type: 'get-rectangle'});
    let resp = await this.waitForResponse(null, {timeout: 3000});
    if (this._stale(session)) return;
    if (!resp?.value) return this._handleMissingElement(`"${selector}" index: ${selectorIndex}`, continueOnError);

    if (scroll) {
      this.sendMessage({selector, selectorIndex, type: 'scroll-to-element'});
      await this.sleep(500);
      if (this._stale(session)) return;

      this.sendMessage({selector, selectorIndex, type: 'get-rectangle'});
      resp = await this.waitForResponse(null, {timeout: 3000});
      if (this._stale(session)) return;
    }

    this.showHighlight(resp?.value, 'rectangle', message);

    await this.waitForHighlightClick();
    if (this._stale(session)) return;
    this.sendMessage({selector, selectorIndex, type: 'keyboard', value});
    this.clearHighlight();

    return value;
  }

  async setSelectorValue({selector, selectorIndex = 0, value = '', scroll = true}) {
    if (scroll) {
      this.sendMessage({selector, selectorIndex, type: 'scroll-to-element'});
      await this.sleep(500);
    }
    this.sendMessage({selector, selectorIndex, type: 'keyboard', value});
  }

  async clickElement({selector, selectorIndex = 0, textValue = null, scroll = true}) {
    if (scroll) {
      if (textValue) {
        this.sendMessage({selector, selectorIndex, type: 'scroll-to-element-by-text', value: textValue});
      } else {
        this.sendMessage({selector, selectorIndex, type: 'scroll-to-element'});
      }
      await this.sleep(500);
    }
    if (textValue) {
      this.sendMessage({selector, selectorIndex, type: 'click-element-by-text', value: textValue});
    } else {
      this.sendMessage({selector, selectorIndex, type: 'click-element'});
    }
  }

  async gotoSelectorAndWaitForSetTime({selector, selectorIndex = 0, value = '', message = null, continueOnError = false}) {
    const session = this._sessionId;
    this.sendMessage({selector, selectorIndex, type: 'get-rectangle'});
    const resp = await this.waitForResponse(null, {timeout: 3000});
    if (this._stale(session)) return;
    if (!resp?.value) return this._handleMissingElement(`"${selector}" index: ${selectorIndex}`, continueOnError);
    this.showHighlight(resp.value, 'rectangle', message);

    await this.waitForHighlightClick();
    if (this._stale(session)) return;
    this.sendMessage({selector, selectorIndex, type: 'keyboard', value});
    this.clearHighlight();

    return value;
  }

  async gotoSelectorAndShowMessage({selector, selectorIndex = 0, message, continueOnError = false}) {
    const session = this._sessionId;
    this.sendMessage({selector, selectorIndex, type: 'get-rectangle'});
    const resp = await this.waitForResponse(null, {timeout: 3000});
    if (this._stale(session)) return;
    if (!resp?.value) return this._handleMissingElement(`"${selector}" index: ${selectorIndex}`, continueOnError);
    this.showHighlight(resp.value);
    this.showTooltip(resp.value, message);
    await this.waitForHighlightClick();
    if (this._stale(session)) return;
    this.clearHighlight();
  }

  async getUrl() {
    this.sendMessage({selector: '', type: 'get-url'});
    const resp = await this.waitForResponse((m) => m.type === 'url');
    if (!resp) return null; // skip won the race
    this.log(`Current URL: ${resp.value}`, 'info');

    return resp.value ?? null;
  }

  sendMessage(msg) {
    if (this._aborted) {
      this.log(`✗ dropped ${msg.type} (scenario aborted)`);
      return;
    }

    const full = {
      isOniFrame: false,
      ...msg,
      from: 'puppeteer',
    };

    this.log(`→ ${msg.type} sel="${msg.selector || ''}" val="${msg.value || ''}"`);

    this.iframe.contentWindow.postMessage(full, '*');
  }

  // --- Highlight ---

  showHighlight(rect, shape = 'rectangle', tooltipMessage = null, padding = this.highlightGutter) {
    if (this._aborted) return; // scenario was cancelled; don't re-show
    if (!rect) {
      this.log('No rectangle returned (element not found)');
      return;
    }
    const p = typeof padding === 'number'
      ? {top: padding, right: padding, bottom: padding, left: padding}
      : {top: padding?.top ?? 0, right: padding?.right ?? 0, bottom: padding?.bottom ?? 0, left: padding?.left ?? 0};

    const size = shape === 'circle'
      ? Math.max(rect.width, rect.height) + 2 * Math.max(p.top, p.right, p.bottom, p.left)
      : null;

    gsap.killTweensOf(this.highlightEl);
    this.highlightEl.style.display = 'block';
    this.highlightEl.style.left = (shape === 'circle' ? rect.x + rect.width / 2 - size / 2 : rect.x - p.left) + 'px';
    this.highlightEl.style.top = (shape === 'circle' ? rect.y + rect.height / 2 - size / 2 : rect.y - p.top) + 'px';
    this.highlightEl.style.width = (size ?? (rect.width + p.left + p.right)) + 'px';
    this.highlightEl.style.height = (size ?? (rect.height + p.top + p.bottom)) + 'px';
    this.highlightEl.style.borderRadius = shape === 'circle' ? '50%' : '4px';

    gsap.fromTo(this.highlightEl, {opacity: 0}, {opacity: 1, duration: 0.3, ease: "power2.out"});

    this.log(`Highlight at (${rect.x}, ${rect.y}) ${rect.width}x${rect.height}`);

    if (tooltipMessage) {
      const outer = shape === 'circle'
        ? {x: rect.x + rect.width / 2 - size / 2, y: rect.y + rect.height / 2 - size / 2, width: size, height: size}
        : {x: rect.x - p.left, y: rect.y - p.top, width: rect.width + p.left + p.right, height: rect.height + p.top + p.bottom};
      this.showTooltip(outer, tooltipMessage);
    }
  }

  showTooltip(rect, message) {
    this._renderTooltip(this.tooltipEl, this.tooltipTextEl, rect, message);
  }

  _renderTooltip(el, textEl, rect, message) {
    gsap.killTweensOf(el);
    textEl.innerHTML = message;
    el.style.display = 'block';
    el.className = 'iframe-tooltip';

    const containerRect = this.iframeContainer.getBoundingClientRect();
    const containerW = containerRect.width;
    const containerH = containerRect.height;

    el.style.visibility = 'hidden';
    el.style.left = '0px';
    el.style.top = '0px';
    const tipW = el.offsetWidth;
    const tipH = el.offsetHeight;
    el.style.visibility = '';

    const elCenterY = rect.y + rect.height / 2;
    const elCenterX = rect.x + rect.width / 2;
    const elRight = rect.x + rect.width + this.highlightGutter;
    const elLeft = rect.x - this.highlightGutter;
    const elBottom = rect.y + rect.height + this.highlightGutter;
    const elTop = rect.y - this.highlightGutter;

    const spaceRight = containerW - elRight;
    const spaceLeft = elLeft;
    const spaceBelow = containerH - elBottom;

    let pos;
    if (spaceRight >= tipW + this.tooltipGap) pos = 'right';
    else if (spaceLeft >= tipW + this.tooltipGap) pos = 'left';
    else if (spaceBelow >= tipH + this.tooltipGap) pos = 'below';
    else pos = 'above';

    el.classList.add('pos-' + pos);

    let tipX, tipY;
    switch (pos) {
      case 'right':
        tipX = elRight + this.tooltipGap;
        tipY = elCenterY - tipH / 2;
        break;
      case 'left':
        tipX = elLeft - this.tooltipGap - tipW;
        tipY = elCenterY - tipH / 2;
        break;
      case 'below':
        tipX = elCenterX - tipW / 2;
        tipY = elBottom + this.tooltipGap;
        break;
      case 'above':
        tipX = elCenterX - tipW / 2;
        tipY = elTop - this.tooltipGap - tipH;
        break;
    }

    tipX = Math.max(4, Math.min(tipX, containerW - tipW - 4));
    tipY = Math.max(4, Math.min(tipY, containerH - tipH - 4));

    el.style.left = tipX + 'px';
    el.style.top = tipY + 'px';

    const slideFrom = {x: 0, y: 0};
    if (pos === 'right') slideFrom.x = 10;
    else if (pos === 'left') slideFrom.x = -10;
    else if (pos === 'below') slideFrom.y = 10;
    else if (pos === 'above') slideFrom.y = -10;

    gsap.fromTo(
      el,
      {opacity: 0, x: slideFrom.x, y: slideFrom.y},
      {opacity: 1, x: 0, y: 0, duration: 0.35, ease: "power2.out"}
    );
  }

  clearHighlight() {
    gsap.killTweensOf(this.highlightEl);
    gsap.killTweensOf(this.tooltipEl);
    gsap.to(this.highlightEl, {
      opacity: 0, duration: 0.25, ease: "power2.in",
      onComplete: () => {
        this.highlightEl.style.display = 'none';
      }
    });
    gsap.to(this.tooltipEl, {
      opacity: 0, duration: 0.25, ease: "power2.in",
      onComplete: () => {
        this.tooltipEl.style.display = 'none';
      }
    });
    for (const el of this._extraTooltips) {
      gsap.to(el, {
        opacity: 0, duration: 0.25, ease: "power2.in",
        onComplete: () => el.remove(),
      });
    }
    this._extraTooltips = [];
  }

  waitForResponse(filterFn, {timeout = null} = {}) {
    let timeoutId = null;
    let handler = null;
    const cleanup = () => {
      if (handler) {
        window.removeEventListener('message', handler);
        handler = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const promise = new Promise((resolve) => {
      handler = (event) => {
        const msg = event.data;
        if (msg.from === 'ret-puppeteer' && (!filterFn || filterFn(msg))) {
          cleanup();
          this.log(`← ${msg.type || 'response'} value=${JSON.stringify(msg.value ?? "").slice(0, 120)}`);
          resolve(msg);
        }
      };
      window.addEventListener('message', handler);
      if (timeout) {
        timeoutId = setTimeout(() => {
          cleanup();
          this.log(`← waitForResponse timed out after ${timeout}ms`);
          resolve({value: null, timedOut: true});
        }, timeout);
      }
    });

    // Also clean up when skip wins the race
    this._skipPromise.then(cleanup);

    return this._raceWithSkip(promise);
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  log(message) {
    if (this.debug) {
      console.log(`[${new Date().toLocaleTimeString()}] ${message}`);
    }
  }

}
